import type { CSSProperties } from 'react';
import type { Cita, Profesional } from '../../api/ops/citas';
import { Chip } from '../../componentes/Chip';
import { Icono } from '../../componentes/Icono';
import { sumarDias, diaDeLaSemana } from '../../logica/horario';
import { Avatar } from '../../marco/piezas';
import type { ContextoAgenda } from './contexto';
import { activa, chipsDe, diaCorto, diaLargo, enCurso, franjaDe, hayDia, hm, horaEn, huecosLibres, iniciales, mayuscula, primerNombre, puedeLlegada, puedeNoVino, relativo, textoServicios, tramosDelDia } from './logica';
import { ESTADO } from './textos';

/** Botón de la agenda (rv-ag-b del diseño). */
export function BotonAg({ texto, icono, tipo = 'sec', onClick, disabled }: { texto: string; icono: Parameters<typeof Icono>[0]['nombre']; tipo?: 'sec' | 'rojo' | 'tinta' | 'pri'; onClick: () => void; disabled?: boolean }) {
    const clase = tipo === 'rojo' ? ' rv-ag-b--rojo' : tipo === 'tinta' ? ' rv-ag-b--tinta' : tipo === 'pri' ? ' rv-ag-b--pri' : '';
    return (
        <button type="button" className={`rv-ag-b${clase}`} onClick={onClick} disabled={disabled}>
            <Icono nombre={icono} />
            <span>{texto}</span>
        </button>
    );
}

/** Colores de cada estado en la agenda (el bloque de la rejilla y la tarjeta de la lista). */
function piel(c: Cita, ctx: ContextoAgenda): { fondo: string; tinta: string; sombra: string } {
    const clave = enCurso(c, ctx.ahora) ? 'live' : c.status;
    switch (clave) {
        case 'live':
            return { fondo: '#fff', tinta: 'var(--rv-ink)', sombra: 'inset 0 0 0 2px var(--rv-red)' };
        case 'arrived':
            return { fondo: 'var(--rv-ink)', tinta: '#fff', sombra: 'none' };
        case 'completed':
            return { fondo: 'var(--rv-paper)', tinta: 'var(--rv-fg-2)', sombra: 'none' };
        case 'pending_payment':
            return { fondo: '#fff', tinta: 'var(--rv-ink)', sombra: 'inset 0 0 0 1.5px var(--rv-warning), var(--rv-shadow-1)' };
        case 'pending_completion':
            return { fondo: 'var(--rv-warning-soft)', tinta: 'var(--rv-warning-ink)', sombra: 'none' };
        case 'no_show':
            return { fondo: 'var(--rv-red-soft)', tinta: 'var(--rv-ink)', sombra: 'none' };
        default:
            return { fondo: '#fff', tinta: 'var(--rv-ink)', sombra: 'inset 0 0 0 1px var(--rv-rule-strong), var(--rv-shadow-1)' };
    }
}

// ---- Por completar (G14) ----

export function PorCompletar({ ctx }: { ctx: ContextoAgenda }) {
    const citas = ctx.datos.porCompletar;
    if (citas.length === 0) {
        return null;
    }
    return (
        <section className="rv-ag-pc" aria-label="Por completar">
            <div className="rv-ag-pc__cab">
                <Icono nombre="circle-alert" />
                <strong>{citas.length === 1 ? '1 cita por completar' : `${String(citas.length)} citas por completar`}</strong>
                <span>Terminaron sin que nadie marcara la llegada. Indica si vinieron.</span>
            </div>
            {citas.map((c) => {
                const nombre = ctx.nombreDe(c);
                const pro = ctx.profesional(c.segments[0]?.professional_id);
                const linea = `${relativo(c.local_date, ctx.ahora.fecha)}, ${hm(franjaDe(c).inicio)} · ${textoServicios(c)}${ctx.esProfesional || pro === undefined ? '' : ` · con ${primerNombre(pro.display_name)}`}`;
                return (
                    <div key={c.id} className="rv-ag-pc__fila">
                        <button
                            type="button"
                            className="rv-ag-pc__quien"
                            onClick={() => {
                                ctx.abrirCita(c.id);
                            }}
                        >
                            <Avatar texto={iniciales(nombre)} tamano={36} fondo="var(--rv-ink)" tinta="#fff" />
                            <span>
                                <strong>{nombre}</strong>
                                <small>{linea}</small>
                            </span>
                        </button>
                        {ctx.puede.completar && !ctx.soloLectura && (
                            <BotonAg
                                texto="Vino"
                                icono="check"
                                onClick={() => {
                                    ctx.marcarVino(c);
                                }}
                            />
                        )}
                        {ctx.puede.noShow && !ctx.soloLectura && (
                            <BotonAg
                                texto="No vino"
                                icono="user-x"
                                tipo="rojo"
                                onClick={() => {
                                    ctx.pedirNoVino(c);
                                }}
                            />
                        )}
                    </div>
                );
            })}
        </section>
    );
}

// ---- Firmas pendientes (G960, «Firmas en el centro» de Inicio: `?firma=pendiente`) ----

export function FirmasPendientes({ ctx }: { ctx: ContextoAgenda }) {
    const citas = ctx.datos.firmasPendientes;
    if (citas.length === 0) {
        return null;
    }
    return (
        <section className="rv-ag-pc" aria-label="Firmas pendientes">
            <div className="rv-ag-pc__cab">
                <Icono nombre="signature" />
                <strong>{citas.length === 1 ? '1 cita con la firma pendiente' : `${String(citas.length)} citas con la firma pendiente`}</strong>
                <span>El cliente firma en la tablet al llegar.</span>
            </div>
            {citas.map((c) => {
                const nombre = ctx.nombreDe(c);
                const pro = ctx.profesional(c.segments[0]?.professional_id);
                const linea = `${relativo(c.local_date, ctx.ahora.fecha)}, ${hm(franjaDe(c).inicio)} · ${textoServicios(c)}${ctx.esProfesional || pro === undefined ? '' : ` · con ${primerNombre(pro.display_name)}`}`;
                return (
                    <div key={c.id} className="rv-ag-pc__fila">
                        <button
                            type="button"
                            className="rv-ag-pc__quien"
                            onClick={() => {
                                ctx.abrirCita(c.id);
                            }}
                        >
                            <Avatar texto={iniciales(nombre)} tamano={36} fondo="var(--rv-ink)" tinta="#fff" />
                            <span>
                                <strong>{nombre}</strong>
                                <small>{linea}</small>
                            </span>
                        </button>
                    </div>
                );
            })}
        </section>
    );
}

// ---- Rejilla de ordenador ----

const INICIO = 9 * 60;
const FIN = 20 * 60;
const HORA_PX = 88;
const PX = HORA_PX / 60;

/** Las horas que se ven: de 09:00 a 20:00, o más si algún tramo o cita se sale. */
function margenes(ctx: ContextoAgenda, columnas: { pro: Profesional; fecha: string }[]): [number, number] {
    let ini = INICIO;
    let fin = FIN;
    for (const { pro, fecha } of columnas) {
        for (const [a, b] of tramosDelDia(ctx.datos.horas[pro.id], fecha)) {
            ini = Math.min(ini, a);
            fin = Math.max(fin, Math.min(b, 24 * 60));
        }
    }
    for (const c of ctx.datos.citas) {
        const f = franjaDe(c);
        ini = Math.min(ini, f.inicio);
        fin = Math.max(fin, Math.min(f.fin, 24 * 60));
    }
    return [Math.floor(ini / 60) * 60, Math.ceil(fin / 60) * 60];
}

function citasDe(ctx: ContextoAgenda, pro: Profesional, fecha: string): Cita[] {
    return ctx.datos.citas.filter((c) => c.local_date === fecha && activa(c) && c.segments.some((s) => s.professional_id === pro.id));
}

export function Rejilla({ ctx, vista, fecha, semanaDe }: { ctx: ContextoAgenda; vista: 'dia' | 'semana'; fecha: string; semanaDe: Profesional | undefined }) {
    const columnas =
        vista === 'dia'
            ? ctx.datos.profesionales.map((pro) => ({ pro, fecha }))
            : semanaDe === undefined
              ? []
              : [0, 1, 2, 3, 4, 5, 6].map((i) => ({ pro: semanaDe, fecha: sumarDias(fecha, i) }));
    const [ini, fin] = margenes(ctx, columnas);
    const alto = (fin - ini) * PX;
    const minCol = vista === 'semana' ? 128 : 200;
    const hayAhora = columnas.some((c) => c.fecha === ctx.ahora.fecha) && ctx.ahora.minutos > ini && ctx.ahora.minutos < fin;
    const top = (m: number) => (Math.max(m, ini) - ini) * PX;

    return (
        <>
            <div className="rv-ag-rejilla">
                <div className="rv-ag-rejilla__in" style={{ gridTemplateColumns: `56px repeat(${String(Math.max(1, columnas.length))}, minmax(${String(minCol)}px, 1fr))`, minWidth: 56 + Math.max(1, columnas.length) * minCol }}>
                    <div className="rv-ag-col__cab" style={{ borderLeft: 0 }} />
                    {columnas.map(({ pro, fecha: f }) => {
                        const tramos = tramosDelDia(ctx.datos.horas[pro.id], f);
                        const sabe = hayDia(ctx.datos.horas[pro.id], f);
                        const n = citasDe(ctx, pro, f).length;
                        const libres = huecosLibres(tramos, citasDe(ctx, pro, f), f, ctx.ahora);
                        const cerrado = sabe && tramos.length === 0;
                        const domingo = diaDeLaSemana(f) === 6;
                        return vista === 'dia' ? (
                            <div key={pro.id} className="rv-ag-col__cab">
                                <Avatar texto={iniciales(pro.display_name)} tamano={32} fondo={pro.color ?? 'var(--rv-ink)'} tinta="#fff" />
                                <span>
                                    <strong>{pro.display_name}</strong>
                                    <small>{cerrado ? 'No trabaja este día' : `${n === 1 ? '1 cita' : `${String(n)} citas`}${sabe ? ` · ${libres.length === 1 ? '1 hueco' : `${String(libres.length)} huecos`}` : ''}`}</small>
                                </span>
                            </div>
                        ) : (
                            <div key={f} className="rv-ag-col__cab">
                                <span className={`rv-ag-col__num${f === ctx.ahora.fecha ? ' rv-ag-col__num--hoy' : ''}`}>{f.slice(8).replace(/^0/, '')}</span>
                                <span>
                                    <strong>{mayuscula(diaLargo(f).split(',')[0] ?? '')}</strong>
                                    <small>{cerrado ? (domingo ? 'Cerrado' : 'No trabaja') : n === 1 ? '1 cita' : `${String(n)} citas`}</small>
                                </span>
                            </div>
                        );
                    })}
                    <div className="rv-ag-horas" style={{ height: alto }}>
                        {Array.from({ length: (fin - ini) / 60 }, (_, i) => (
                            <span key={i} style={{ top: Math.max(2, i * HORA_PX - 7) }}>
                                {hm(ini + i * 60)}
                            </span>
                        ))}
                        {hayAhora && (
                            <span className="rv-ag-ahora__hora" style={{ top: top(ctx.ahora.minutos) - 9, left: 4 }}>
                                {hm(ctx.ahora.minutos)}
                            </span>
                        )}
                    </div>
                    {columnas.map(({ pro, fecha: f }) => {
                        const tramos = tramosDelDia(ctx.datos.horas[pro.id], f);
                        const sabe = hayDia(ctx.datos.horas[pro.id], f);
                        const fuera: [number, number][] = [];
                        if (sabe) {
                            let t = ini;
                            for (const [a, b] of tramos) {
                                if (a > t) {
                                    fuera.push([t, Math.min(a, fin)]);
                                }
                                t = Math.max(t, b);
                            }
                            if (t < fin) {
                                fuera.push([t, fin]);
                            }
                        }
                        const citas = citasDe(ctx, pro, f);
                        const libres = sabe ? huecosLibres(tramos, citas, f, ctx.ahora).filter(([a]) => a < fin) : [];
                        return (
                            <div key={`${pro.id}${f}`} className="rv-ag-col" style={{ height: alto, backgroundSize: `100% ${String(HORA_PX)}px` }}>
                                {fuera.filter(([a, b]) => b > a).map(([a, b]) => (
                                    <div key={a} className="rv-ag-fuera" style={{ top: top(a), height: (b - a) * PX }} />
                                ))}
                                {libres.map(([a, b]) => {
                                    const estilo: CSSProperties = { top: top(a) + 1.5, height: (Math.min(b, fin) - a) * PX - 3 };
                                    return ctx.puede.crear && !ctx.soloLectura ? (
                                        <button
                                            key={a}
                                            type="button"
                                            className="rv-ag-libre"
                                            style={estilo}
                                            aria-label={`Hueco libre de ${hm(a)} a ${hm(b)} con ${primerNombre(pro.display_name)}`}
                                            onClick={() => {
                                                ctx.nuevaCita({ fecha: f, hora: a, profesional: pro.id });
                                            }}
                                        >
                                            <Icono nombre="plus" tamano={14} />
                                            <span>{`Libre · ${hm(a)}`}</span>
                                        </button>
                                    ) : (
                                        <div key={a} className="rv-ag-libre" style={estilo}>{`Libre ${hm(a)} – ${hm(b)}`}</div>
                                    );
                                })}
                                {citas.map((c) => {
                                    const fr = franjaDe(c);
                                    const h = Math.max(30, Math.min(fr.fin, fin) - Math.max(fr.inicio, ini)) * PX - 3;
                                    const p = piel(c, ctx);
                                    const chips = chipsDe(c, ctx.ahora, h > 120 ? 2 : 1);
                                    const compacta = h < 56;
                                    const nombre = ctx.nombreDe(c);
                                    const llego = c.status === 'arrived' && c.arrived_at != null ? ` · llegó ${horaEn(c.arrived_at, ctx.zona)}` : '';
                                    return (
                                        <button
                                            key={c.id}
                                            type="button"
                                            className="rv-ag-bloque"
                                            style={{ top: top(fr.inicio) + 1.5, height: h, background: p.fondo, color: p.tinta, boxShadow: p.sombra, justifyContent: compacta ? 'center' : 'flex-start', padding: compacta ? '0 10px' : '7px 10px' }}
                                            aria-label={`${hm(fr.inicio)}, ${nombre}, ${textoServicios(c)}, ${enCurso(c, ctx.ahora) ? 'ahora' : ESTADO[c.status][0]}`}
                                            onClick={() => {
                                                ctx.abrirCita(c.id);
                                            }}
                                        >
                                            {compacta ? (
                                                <span className="rv-ag-bloque__nombre">
                                                    {hm(fr.inicio)} {nombre}
                                                </span>
                                            ) : (
                                                <>
                                                    <span className="rv-ag-bloque__hora">{`${hm(fr.inicio)} – ${hm(fr.fin)}${llego}`}</span>
                                                    <span className="rv-ag-bloque__nombre">{nombre}</span>
                                                    {h >= 84 && !(chips.length > 0 && h < 110) && <span className="rv-ag-bloque__svc">{textoServicios(c)}</span>}
                                                    {chips.length > 0 && h >= 84 && (
                                                        <span className="rv-ag-bloque__chips">
                                                            {chips.map((x) => (
                                                                <Chip key={x.texto} estado={x.estado}>
                                                                    {x.texto}
                                                                </Chip>
                                                            ))}
                                                        </span>
                                                    )}
                                                </>
                                            )}
                                        </button>
                                    );
                                })}
                                {hayAhora && f === ctx.ahora.fecha && <div className="rv-ag-ahora" style={{ top: top(ctx.ahora.minutos) }} />}
                            </div>
                        );
                    })}
                </div>
            </div>
            <Leyenda />
        </>
    );
}

const LEYENDA: readonly (readonly [string, string, string])[] = [
    ['Confirmada', '#fff', 'inset 0 0 0 1px var(--rv-rule-strong)'],
    ['Ahora', '#fff', 'inset 0 0 0 2px var(--rv-red)'],
    ['Llegó', 'var(--rv-ink)', 'none'],
    ['Pendiente de pago', '#fff', 'inset 0 0 0 1.5px var(--rv-warning)'],
    ['Por completar', 'var(--rv-warning-soft)', 'none'],
    ['Completada', 'var(--rv-paper)', 'none'],
    ['No presentado', 'var(--rv-red-soft)', 'none'],
];

function Leyenda() {
    return (
        <div className="rv-ag-leyenda" aria-hidden="true">
            {LEYENDA.map(([t, fondo, sombra]) => (
                <span key={t}>
                    <i style={{ background: fondo, boxShadow: sombra }} />
                    {t}
                </span>
            ))}
            <span>
                <i style={{ border: '1.5px dashed var(--rv-rule-strong)' }} />
                Hueco libre
            </span>
            <span>
                <i style={{ background: 'repeating-linear-gradient(135deg, var(--rv-bone) 0 3px, var(--rv-paper) 3px 4px)' }} />
                No trabaja
            </span>
        </div>
    );
}

// ---- Lista por hora (móvil, iPad vertical y Mi agenda) ----

export function ListaHora({ ctx, dias, profesional, conTitulo }: { ctx: ContextoAgenda; dias: readonly string[]; profesional: Profesional | null; conTitulo: boolean }) {
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
            {dias.map((fecha) => (
                <Seccion key={fecha} ctx={ctx} fecha={fecha} profesional={profesional} conTitulo={conTitulo} conLibres={dias.length === 1} />
            ))}
        </div>
    );
}

function Seccion({ ctx, fecha, profesional, conTitulo, conLibres }: { ctx: ContextoAgenda; fecha: string; profesional: Profesional | null; conTitulo: boolean; conLibres: boolean }) {
    const citas = ctx.datos.citas.filter((c) => c.local_date === fecha && (profesional === null || c.segments.some((s) => s.professional_id === profesional.id)) && (activa(c) || conLibres));
    const tramos = profesional === null ? [] : tramosDelDia(ctx.datos.horas[profesional.id], fecha);
    const sabe = profesional === null ? ctx.datos.profesionales.some((p) => hayDia(ctx.datos.horas[p.id], fecha)) : hayDia(ctx.datos.horas[profesional.id], fecha);
    const trabaja = profesional === null ? ctx.datos.profesionales.some((p) => tramosDelDia(ctx.datos.horas[p.id], fecha).length > 0) : tramos.length > 0;
    const libres = profesional !== null && conLibres ? huecosLibres(tramos, citas, fecha, ctx.ahora) : [];
    type Fila = { k: number; c: Cita; g?: undefined } | { k: number; c?: undefined; g: [number, number] };
    const filas: Fila[] = [...citas.map((c): Fila => ({ k: franjaDe(c).inicio, c })), ...libres.map((g): Fila => ({ k: g[0], g }))].sort((a, b) => a.k - b.k);
    const n = citas.filter(activa).length;
    const domingo = diaDeLaSemana(fecha) === 6;
    const cerrado = sabe && !trabaja;
    const titulo = fecha === ctx.ahora.fecha ? `Hoy, ${diaCorto(fecha).replace(/^\S+ /, '')}` : diaLargo(fecha);
    return (
        <section className="rv-ag-seccion">
            {conTitulo && (
                <div className="rv-ag-seccion__cab">
                    <strong>{titulo}</strong>
                    <small>{cerrado ? (domingo ? 'Cerrado' : 'No trabaja') : n === 1 ? '1 cita' : `${String(n)} citas`}</small>
                </div>
            )}
            {filas.length === 0 && (
                <div className="rv-ag-vacio">{cerrado ? (domingo ? 'Cerrado.' : ctx.esProfesional ? 'No trabajas este día.' : 'No trabaja este día.') : ctx.esProfesional ? 'No tienes citas este día.' : 'No hay citas este día.'}</div>
            )}
            {filas.map((x) => (x.c !== undefined ? <FilaCita key={x.c.id} ctx={ctx} c={x.c} conProfesional={profesional === null} /> : <FilaLibre key={`l${String(x.g[0])}`} ctx={ctx} hueco={x.g} fecha={fecha} profesional={profesional} />))}
        </section>
    );
}

function FilaCita({ ctx, c, conProfesional }: { ctx: ContextoAgenda; c: Cita; conProfesional: boolean }) {
    const fr = franjaDe(c);
    const p = piel(c, ctx);
    const pro = ctx.profesional(c.segments[0]?.professional_id);
    const chips = chipsDe(c, ctx.ahora, 2);
    const llego = c.status === 'arrived' && c.arrived_at != null ? `llegó a las ${horaEn(c.arrived_at, ctx.zona)}` : null;
    const linea = [textoServicios(c), conProfesional && !ctx.esProfesional && pro !== undefined ? `con ${primerNombre(pro.display_name)}` : null, llego].filter((x) => x !== null && x !== '').join(' · ');
    const botones: { texto: string; icono: 'log-in' | 'check' | 'user-x'; tipo: 'tinta' | 'sec' | 'rojo'; fn: () => void }[] = [];
    if (ctx.puede.llegada && puedeLlegada(c, ctx.ahora.fecha) && fr.inicio - 60 <= ctx.ahora.minutos) {
        botones.push({ texto: 'Llegó', icono: 'log-in', tipo: 'tinta', fn: () => { ctx.registrarLlegada(c); } });
    }
    if (c.status === 'pending_completion' && ctx.puede.completar && !ctx.soloLectura) {
        botones.push({ texto: 'Vino', icono: 'check', tipo: 'sec', fn: () => { ctx.marcarVino(c); } });
    }
    if (ctx.puede.noShow && !ctx.soloLectura && puedeNoVino(c) && c.local_date === ctx.ahora.fecha) {
        botones.push({ texto: 'No vino', icono: 'user-x', tipo: 'rojo', fn: () => { ctx.pedirNoVino(c); } });
    }
    return (
        <div className="rv-ag-fila">
            <div className="rv-ag-fila__hora" style={{ color: c.status === 'completed' ? 'var(--rv-fg-3)' : 'var(--rv-ink)' }}>
                {hm(fr.inicio)}
                <small>{hm(fr.fin)}</small>
            </div>
            <div className="rv-ag-tarjeta" style={{ background: p.fondo, color: p.tinta, boxShadow: p.sombra === 'none' ? 'none' : 'var(--rv-shadow-2)' }}>
                <button
                    type="button"
                    className="rv-ag-tarjeta__abrir"
                    onClick={() => {
                        ctx.abrirCita(c.id);
                    }}
                >
                    <div>
                        <strong>{ctx.nombreDe(c)}</strong>
                        <small>{linea}</small>
                    </div>
                    <Icono nombre="chevron-right" />
                </button>
                {chips.length > 0 && (
                    <span className="rv-ag-bloque__chips">
                        {chips.map((x) => (
                            <Chip key={x.texto} estado={x.estado}>
                                {x.texto}
                            </Chip>
                        ))}
                    </span>
                )}
                {botones.length > 0 && (
                    <div className="rv-ag-tarjeta__botones">
                        {botones.map((b) => (
                            <BotonAg key={b.texto} texto={b.texto} icono={b.icono} tipo={b.tipo} onClick={b.fn} />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

function FilaLibre({ ctx, hueco, fecha, profesional }: { ctx: ContextoAgenda; hueco: [number, number]; fecha: string; profesional: Profesional | null }) {
    const [a, b] = hueco;
    const puede = ctx.puede.crear && !ctx.esProfesional && !ctx.soloLectura;
    return (
        <div className="rv-ag-fila">
            <div className="rv-ag-fila__hora" style={{ color: 'var(--rv-fg-3)' }}>
                {hm(a)}
                <small>{hm(b)}</small>
            </div>
            {puede ? (
                <button
                    type="button"
                    className="rv-ag-libre"
                    style={{ position: 'static', minHeight: 48, alignItems: 'center' }}
                    onClick={() => {
                        ctx.nuevaCita({ fecha, hora: a, profesional: profesional?.id });
                    }}
                >
                    <Icono nombre="plus" tamano={14} />
                    <span>{`Libre de ${hm(a)} a ${hm(b)}`}</span>
                </button>
            ) : (
                <div className="rv-ag-libre" style={{ position: 'static', minHeight: 48, alignItems: 'center' }}>{`Libre de ${hm(a)} a ${hm(b)}`}</div>
            )}
        </div>
    );
}

